/**
 * Extracts a numeric data allowance value from a given
 * string that is directly followed by the term "GB".
 * Example: 'Halebop 100GB' -> '100'
 */
const _extractDataAllowanceFromString = (text: string): string | undefined => {
  const numberFollowedByOptionalWhitespaceAndGB = /(\d+)\s*GB/i;
  const match = text.match(numberFollowedByOptionalWhitespaceAndGB);
  return match ? match[1] : undefined;
};

/**
 * Generates an image URL string for a subscription item based on its
 * data amount, product name or if it's a family plan by priority
 */
export const selectImageUrlForSubscriptionItemInList = (
  dataAllowanceSize?: string,
  regularDataAllowanceSize?: string,
  productName?: string,
  isFamilyPlan: boolean = false,
  isMobileBrodband: boolean = false,
): string | undefined => {
  const config = useRuntimeConfig();

  if (isFamilyPlan) {
    return `${config.public.publicCloudfrontDistribution}/images/subscriptions/family_small.svg`;
  }

  if (isMobileBrodband) {
    return `${config.public.publicCloudfrontDistribution}/images/subscriptions/mobile_broadband_small.svg`;
  }

  // If regularDataAllowance has a value, this is a campaign plan and we
  // don't show the plan image for the original data size, not the extra data size
  let dataSize = regularDataAllowanceSize
    ? regularDataAllowanceSize
    : dataAllowanceSize;

  // If we don't have data allowance as a data field on the item but
  // it's implied in the product name, try to parse it from the product name
  if (!dataSize && productName) {
    dataSize = _extractDataAllowanceFromString(productName);
  }

  if (dataSize) {
    return `${config.public.publicCloudfrontDistribution}/images/subscriptions/${dataSize}_small.svg`;
  }

  return undefined;
};

/**
 * Generates an image URL string for a pre paid item based on its
 * name and onetimeprice
 *
 * @param {string} [productName] - The name of the product which may contain a data allowance
 * example: Fastpris 20 GB utan bindningstid
 * @param {string} [price] - The onetimeprice of the product
 * example: 275
 * @returns {string | undefined} - The formatted image URL or undefined if no valid data allowance is found.
 */
export const makeImageUrlStringForPrePaidItem = (
  productName?: string,
  price?: number,
): string | undefined => {
  const config = useRuntimeConfig();

  const data = productName
    ? _extractDataAllowanceFromString(productName)
    : undefined;
  if (data && price != 0) {
    return `${config.public.publicCloudfrontDistribution}/images/prepaid/fastpris_${data}_small.png`;
  } else if (!data && price == 0) {
    return `${config.public.publicCloudfrontDistribution}/images/prepaid/startpaket_small.png`;
  } else {
    return `${config.public.publicCloudfrontDistribution}/images/prepaid/saldo_${price?.toString()}_small.png`;
  }
};
